import { Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import UserContext from "../../context/userContext";
import TwoFactorVerification from "../../components/auth/TwoFactorVerification";
import Configure2FA from "../../modules/authentication/Configure2FA";
import { handleLoggedInRoute } from "../../router/routeHelper";
import routeNames from "../../router/routeNames";
import useJwt from "../../services/useJwt";
import Message from "../../utility/notifications/Message";
import AppButton from "../components/Common/AppButton";
import AppCheckboxInput from "../components/forms/AppCheckboxInput";
import AppTextInput from "../components/forms/AppTextInput";
import { TwoFactorPreference } from "../../types/apiTypes";
import useAuthFromToken from "../../utility/hooks/useAuthFromToken";
import { IsAllowedOfficeDomainResult } from "../../types";
import { AxiosResponse } from "axios";
import UpdatePassword from "../../components/auth/UpdatePassword";

const validationSchema = Yup.object().shape({
  userNameOrEmailAddress: Yup.string().required().label("Emailaddress"),
  password: Yup.string().required().label("password"),
  rememberClient: Yup.bool().label("Remember me"),
});

const officeSchema = Yup.object().shape({
  userNameOrEmailAddress: Yup.string().required().label("Emailaddress"),
});

const Login = () => {
  const userContext = useContext(UserContext);
  let navigate = useNavigate();
  const formMicrosoftRef = useRef<HTMLFormElement>(null);
  const formGoogleRef = useRef<HTMLFormElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [noOfficeDomain, setNoOfficeDomain] = useState<boolean | undefined>();
  const [loading, setLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FAVerification, setShow2FAVerification] = useState(false);
  const [showPasswordUpdateFlow, setShowPasswordUpdateFlow] = useState(false);
  const { isProcessing: isProcessingToken } = useAuthFromToken();
  const [loginCredentials, setLoginCredentials] = useState<{
    userNameOrEmailAddress: string;
    password: string;
    rememberClient: boolean;
  } | null>(null);
  const [twoFactorData, setTwoFactorData] = useState<{
    setupGoogleAuthenticatorImageUrl?: string;
    googleAuthenticatorKey?: string;
    setupAuthenticatorManualEntryKey?: string;
    twoFactorType?: TwoFactorPreference;
  } | null>(null);

  useEffect(() => {
    if (userContext.currentUser) {
      navigate(handleLoggedInRoute());
    }
  }, [userContext.currentUser, navigate]);

  async function handleEmailCheck(data: { userNameOrEmailAddress: string }) {
    setIsCheckingEmail(true);
    try {
      await useJwt
        .checkOAuthMail({ email: data?.userNameOrEmailAddress })
        .then((response: AxiosResponse<IsAllowedOfficeDomainResult>) => {
          console.log(response?.data?.result)
          if (response?.data?.result?.isOfficeDomain === true) {
            return handleOfficeDomain();
          }
          if (response?.data?.result?.isGoogleDomain === true) {
            return handleGoogleDomain();
          }

          setIsCheckingEmail(false);

          return setNoOfficeDomain(true);
        });
    } catch {
      setIsCheckingEmail(false);
    }
  }

  useEffect(() => {
    if (noOfficeDomain && passwordInputRef.current) {
      passwordInputRef.current.focus(); // Focus on the password input when noOfficeDomain is true
    }
  }, [noOfficeDomain]);

  const handleSubmit = (user) => {
    setLoading(true);
    setLoginCredentials(user);

    useJwt
      .login(user)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res.data.result;

          // Check if 2FA setup is required
          if (
            responseData.twoFactorRequired &&
            responseData.twoFactorSetupRequired
          ) {
            setTwoFactorData({
              setupGoogleAuthenticatorImageUrl:
                responseData.setupGoogleAuthenticatorImageUrl,
              googleAuthenticatorKey: responseData.googleAuthenticatorKey,
              setupAuthenticatorManualEntryKey:
                responseData.setupAuthenticatorManualEntryKey,
            });
            setShowPasswordUpdateFlow(responseData.passwordNeedsToBeUpdated)
            setShow2FASetup(true);
            setLoading(false);
            return;
          }

          // Check if 2FA verification is required (user already has 2FA configured)
          if (
            responseData.twoFactorRequired &&
            !responseData.twoFactorSetupRequired
          ) {
            setTwoFactorData({
              twoFactorType: responseData.twoFactorType || "authenticator",
            });
            setShowPasswordUpdateFlow(responseData.passwordNeedsToBeUpdated)
            setShow2FAVerification(true);
            setLoading(false);
            return;
          }

          if (responseData.passwordNeedsToBeUpdated) {
            setShowPasswordUpdateFlow(true)
            return setLoading(false)
          }

          // Normal login flow
          const data = {
            ...responseData.user,
            accessToken: responseData.accessToken,
            refreshToken: responseData.refreshToken,
          };

          userContext.onLoggedIn(data).then(() => {
            navigate(handleLoggedInRoute());
            setLoading(false);
          });
        }
      })
      .catch((err) => {
        if (err.response) {
          setLoading(false);
          Message(
            err.response.data.error.message,
            err.response.data.error.details,
            "error"
          );
        }
      });
  };

  const handle2FASuccess = (data) => {
    // Handle successful 2FA setup or verification
    const userData = {
      ...data.result.user,
      accessToken: data.result.accessToken,
      refreshToken: data.result.refreshToken,
    };

    userContext.onLoggedIn(userData).then(() => {
      navigate(handleLoggedInRoute());
      setShow2FASetup(false);
      setShow2FAVerification(false);
      setLoading(false);
    });
  };

  const handleBackToLogin = () => {
    setShow2FASetup(false);
    setShow2FAVerification(false);
    setLoginCredentials(null);
    setTwoFactorData(null);
    setLoading(false);
  };

  // Show 2FA setup component
  if (show2FASetup && loginCredentials) {
    return (
      <Configure2FA
        setupGoogleAuthenticatorImageUrl={
          twoFactorData?.setupGoogleAuthenticatorImageUrl
        }
        googleAuthenticatorKey={twoFactorData?.googleAuthenticatorKey}
        setupAuthenticatorManualEntryKey={
          twoFactorData?.setupAuthenticatorManualEntryKey
        }
        email={loginCredentials.userNameOrEmailAddress}
        password={loginCredentials.password}
        onSuccess={handle2FASuccess}
      />
    );
  }

  // Show 2FA verification component
  if (show2FAVerification && loginCredentials) {
    return (
      <TwoFactorVerification
        showPasswordUpdateFlow={showPasswordUpdateFlow}
        username={loginCredentials.userNameOrEmailAddress}
        password={loginCredentials.password}
        twoFactorType={
          (twoFactorData?.twoFactorType as TwoFactorPreference) ||
          TwoFactorPreference.Value1
        }
        rememberClient={loginCredentials.rememberClient}
        onSuccess={handle2FASuccess}
        onBack={handleBackToLogin}
      />
    );
  }

  if (showPasswordUpdateFlow && loginCredentials) {
    return <UpdatePassword
        username={loginCredentials.userNameOrEmailAddress}
        password={loginCredentials.password}
        rememberClient={loginCredentials.rememberClient}
        onSuccess={handle2FASuccess}
        onBack={handleBackToLogin}
    />
  }

  async function handleOfficeDomain() {
    try {
      if (formMicrosoftRef.current) {
        formMicrosoftRef.current.submit();
        setIsCheckingEmail(false);
      }
    } catch (error) {
      setIsCheckingEmail(false);
    }
  }

  async function handleGoogleDomain() {
    try {
      if (formGoogleRef.current) {
        formGoogleRef.current.submit();
        setIsCheckingEmail(false);
      }
    } catch (error) {
      setIsCheckingEmail(false);
    }
  }

  // Show normal login form
  return (
    <>
      <div>
        <form
          ref={formMicrosoftRef}
          method="POST"
          action={`${process.env.REACT_APP_API_BASE_URL}TokenAuth/ExternalLogin?provider=Microsoft`}
          style={{ display: "none" }} // Hide the form
        >
          <button
            type="submit"
            name="provider"
            value="Microsoft"
            title="Login using Microsoft"
          >
            <img src="link-to-microsoft-logo" alt="" />
            Microsoft
          </button>
        </form>
      </div>
      <div>
        <form
          ref={formGoogleRef}
          method="POST"
          action={`${process.env.REACT_APP_API_BASE_URL}TokenAuth/ExternalLogin?provider=Google`}
          style={{ display: "none" }} // Hide the form
        >
          <button
            type="submit"
            name="provider"
            value="Google"
            title="Login using Google"
          >
            <img src="link-to-microsoft-logo" alt="" />
            Google
          </button>
        </form>
      </div>

      <h1 className="h3 mb-3 fw-bold text-primary">Login</h1>

      {noOfficeDomain === undefined && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            userNameOrEmailAddress: "",
          }}
          onSubmit={handleEmailCheck}
          validationSchema={officeSchema}
        >
          {({ handleSubmit }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <AppTextInput
                    callback={(e) => setEmail(e)}
                    type="text"
                    id="username"
                    name="userNameOrEmailAddress"
                    label="Emailaddress"
                  />
                </div>

                <AppButton
                  loading={isCheckingEmail || isProcessingToken}
                  disabled={isCheckingEmail || isProcessingToken}
                  type="submit"
                  title="Sign in"
                  buttonSize="lg"
                  className="w-100"
                />
              </Form>
            );
          }}
        </Formik>
      )}

      {noOfficeDomain && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            userNameOrEmailAddress: email,
            password: "",
            rememberClient: true,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ handleSubmit }) => (
            <>
              <Form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <AppTextInput
                    type="text"
                    id="username"
                    name="userNameOrEmailAddress"
                    label="Emailaddress"
                  />
                </div>

                <div className="mb-3">
                  <AppTextInput
                    innerRef={passwordInputRef}
                    id="password"
                    type="password"
                    name="password"
                    label="Password"
                  />
                </div>

                <Row className="row mb-4">
                  <Col className="d-flex justify-content-between">
                    <AppCheckboxInput name="rememberMe" label="Remember me" />
                    <Link to={routeNames.forgot_password}>
                      Forgot password?
                    </Link>
                  </Col>
                </Row>

                <AppButton
                  loading={loading}
                  disabled={loading}
                  type="submit"
                  title="Sign in"
                  buttonSize="lg"
                  className="w-100"
                />
              </Form>
            </>
          )}
        </Formik>
      )}
    </>
  );
};

export default Login;
